import React, { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('reset_password_token')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const isFieldMissing = (val) => submitted && !val

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitted(true)
    setError(null)

    if (!password || !confirmPassword) return

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      const res = await fetch('/api/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            reset_password_token: token,
            password,
            password_confirmation: confirmPassword,
          },
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.errors?.join(', ') || 'Failed to reset password')
      }

      setSuccess(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="bg-surface border-[1.5px] border-ink p-6 text-center">
            <p className="text-sm text-accent-dark mb-4">Invalid or missing reset token.</p>
            <Link
              to="/forgot-password"
              className="text-sm text-accent hover:text-accent-dark"
            >
              Request a new reset link
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png" alt="MyWrenchLog" className="h-16 w-auto mb-3" />
          <h1 className="text-3xl font-bold text-ink">MyWrenchLog</h1>
        </div>

        <div className="bg-surface border-[1.5px] border-ink p-6">
          <h2 className="text-xl font-extrabold text-ink mb-4">Set New Password</h2>

          {success ? (
            <div>
              <p className="text-sm text-ink bg-surface p-3 rounded mb-4">
                Your password has been reset successfully.
              </p>
              <Link
                to="/login"
                className="block w-full py-2 px-4 text-sm font-medium text-center text-white bg-accent rounded-md hover:bg-accent-dark"
              >
                Sign in
              </Link>
            </div>
          ) : (
            <div>
              {error && (
                <div className="text-sm text-accent-dark bg-accent/10 border border-accent/40 p-3 mb-4">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                    className={`w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent ${isFieldMissing(password) ? 'border-accent' : ''}`}
                  />
                  {isFieldMissing(password) && <p className="text-xs text-accent-dark mt-1">Required</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                    className={`w-full border border-gray-400 rounded-md px-3 py-2.5 text-base sm:text-sm focus:ring-2 focus:ring-accent focus:border-accent ${isFieldMissing(confirmPassword) ? 'border-accent' : ''}`}
                  />
                  {isFieldMissing(confirmPassword) && <p className="text-xs text-accent-dark mt-1">Required</p>}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2 px-4 text-sm font-bold text-white bg-accent rounded-md hover:bg-accent-dark disabled:opacity-50"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
